/**
 * TenantSettingsPage — `/settings/tenant` (#2366): the current tenant's
 * branding (#2387), its Shopify and Onfleet integrations, and its settings.
 *
 * Layout (#2429): a section nav (sticky beside the content on large screens,
 * wrapped above it on narrow ones) and three sections — branding /
 * operations / integrations. Each section has a heading, a one-line
 * description and its panel, and reports its own loading and load error.
 *
 * A thin shell over #2364 / #2365; every check that matters is the backend's.
 * Admin-only by route (`<AdminGuard>`) and server-side. Shadow mode and the
 * fulfillment write-back flag stay in env for P1a and are not editable here.
 */
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { PageShell } from '@resource-kit/ui/react';
import { useTenantBranding } from '@hooks/useTenantBranding';
import { useTenantIntegrations, useTenantSettings } from '../hooks/useTenants';
import { OnfleetIntegrationCard, ShopifyIntegrationCard } from '../components/IntegrationCards';
import { TenantSettingsCard } from '../components/TenantSettingsCard';
import { BrandingCard } from '../components/BrandingCard';
import { SettingsSection } from '../components/SettingsSection';

const SECTIONS = [
  {
    id: 'branding',
    titleKey: 'tenants.sections.branding.title',
    descriptionKey: 'tenants.sections.branding.description',
  },
  {
    id: 'operations',
    titleKey: 'tenants.sections.operations.title',
    descriptionKey: 'tenants.sections.operations.description',
  },
  {
    id: 'integrations',
    titleKey: 'tenants.sections.integrations.title',
    descriptionKey: 'tenants.sections.integrations.description',
  },
] as const;

type SectionId = (typeof SECTIONS)[number]['id'];

/** A section's loading / load-error line, shown in place of its panel. */
function SectionStatus({ loading, errorKey }: { loading: boolean; errorKey: string | null }) {
  const { t } = useTranslation();
  if (errorKey !== null) return <p className="text-sm text-destructive">{t(errorKey)}</p>;
  if (loading) return <p className="text-sm text-muted-foreground">{t('tenants.loading')}</p>;
  return null;
}

export default function TenantSettingsPage() {
  const { t } = useTranslation();
  const integrations = useTenantIntegrations();
  const settings = useTenantSettings();
  const branding = useTenantBranding();

  const shopify = integrations.data?.find((status) => status.provider === 'shopify');
  const onfleet = integrations.data?.find((status) => status.provider === 'onfleet');

  const content: Record<SectionId, ReactNode> = {
    branding: (
      <>
        <SectionStatus
          loading={branding.isLoading}
          errorKey={branding.isError ? 'tenants.branding.loadError' : null}
        />
        {branding.data && <BrandingCard branding={branding.data} />}
      </>
    ),
    integrations: (
      <>
        <SectionStatus
          loading={integrations.isLoading}
          errorKey={integrations.isError ? 'tenants.integrations.loadError' : null}
        />
        {shopify && <ShopifyIntegrationCard status={shopify} />}
        {onfleet && <OnfleetIntegrationCard status={onfleet} />}
      </>
    ),
    operations: (
      <>
        <SectionStatus
          loading={settings.isLoading}
          errorKey={settings.isError ? 'tenants.settings.loadError' : null}
        />
        {settings.data && <TenantSettingsCard settings={settings.data} />}
      </>
    ),
  };

  return (
    <PageShell
      title={t('tenants.settingsPage.title')}
      subtitle={t('tenants.settingsPage.subtitle')}
    >
      <div className="grid gap-6 lg:grid-cols-[180px_minmax(0,1fr)] lg:gap-10">
        <nav aria-label={t('tenants.sections.nav')} className="lg:sticky lg:top-6 lg:self-start">
          <ul className="flex flex-wrap gap-1 lg:flex-col">
            {SECTIONS.map((section) => (
              <li key={section.id}>
                <a
                  href={`#tenant-section-${section.id}`}
                  className="block rounded-md px-3 py-1.5 text-sm text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                >
                  {t(section.titleKey)}
                </a>
              </li>
            ))}
          </ul>
        </nav>
        <div className="min-w-0 space-y-10">
          {SECTIONS.map((section) => (
            <SettingsSection
              key={section.id}
              id={section.id}
              title={t(section.titleKey)}
              description={t(section.descriptionKey)}
            >
              <div className="space-y-6">{content[section.id]}</div>
            </SettingsSection>
          ))}
        </div>
      </div>
    </PageShell>
  );
}
